import * as signalR from '@microsoft/signalr';

/**
 * Kopan bağlantılarda süresiz yeniden deneme politikası.
 *
 * withAutomaticReconnect() parametresiz çağrıldığında 0, 2, 10 ve 30. saniyelerde
 * dört deneme yapar; yaklaşık 42 saniye sonra kalıcı olarak vazgeçer. Bu politika
 * hiç null döndürmediği için SignalR denemeyi bırakmaz.
 */
export const infiniteRetryPolicy: signalR.IRetryPolicy = {
  nextRetryDelayInMilliseconds: (retryContext) =>
    retryContext.previousRetryCount < 3 ? 3000 : 10000,
};

export type SignalRStatus = 'Connected' | 'Reconnecting' | 'Disconnected' | 'Error';

export interface ResilientConnectionOptions {
  /**
   * Bağlantı ilk kurulduğunda ve her yeniden bağlanmada çağrılır.
   *
   * Gruba katılma çağrıları buraya konmalı: yeniden bağlanma yeni bir connection id
   * ürettiği için sunucudaki grup üyeliği kaybolur ve istemci bağlı görünmesine
   * rağmen hiçbir yayın almaz.
   */
  onConnected?: () => void | Promise<void>;
  onStatusChange?: (status: SignalRStatus) => void;
  /** Log satırlarında görünecek ad. */
  label?: string;
}

/**
 * Bağlantıyı kurar ve hem ilk kurulumda hem sonraki kopmalarda süresiz yeniden dener.
 *
 * withAutomaticReconnect yalnızca bir kez başarıyla kurulmuş bağlantılar için
 * devreye girer; ilk start başarısız olursa hiç denemez. Buradaki döngü o boşluğu
 * kapatıyor.
 *
 * Döndürülen fonksiyon effect temizliğinde çağrılmalı; bekleyen denemeyi iptal eder
 * ve bağlantıyı kapatır.
 */
export function startResilientConnection(
  connection: signalR.HubConnection,
  options: ResilientConnectionOptions = {}
): () => void {
  const { onConnected, onStatusChange, label = 'SignalR' } = options;

  let disposed = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let failedAttempts = 0;

  /**
   * onConnected başarısız olursa bağlantı açık bırakılmaz. Gruba katılamamış bir
   * istemci "bağlı" görünür ama hiçbir yayın almaz; kapatmak onclose üzerinden
   * yeniden kurulumu tetikler.
   */
  const runOnConnected = async (): Promise<boolean> => {
    if (!onConnected) return true;
    try {
      await onConnected();
      return true;
    } catch (error) {
      console.error(`[${label}] Bağlantı sonrası işlem başarısız, bağlantı yenilenecek:`, error);
      onStatusChange?.('Error');
      void connection.stop().catch(() => {});
      return false;
    }
  };

  const start = async () => {
    retryTimer = null;
    if (disposed) return;

    // Zaten bağlı bir connection verildiyse (örn. birkaç adres denenip başarılı
    // olan seçildiyse) yeniden başlatılmaz, yalnızca dayanıklılık eklenir.
    if (connection.state !== signalR.HubConnectionState.Disconnected) return;

    try {
      await connection.start();
      if (disposed) {
        void connection.stop().catch(() => {});
        return;
      }
      onStatusChange?.('Connected');
      if (await runOnConnected()) {
        failedAttempts = 0;
      }
    } catch (error) {
      if (disposed) return;
      failedAttempts += 1;
      const delay = failedAttempts < 3 ? 3000 : 10000;
      onStatusChange?.('Error');
      console.warn(
        `[${label}] Bağlantı kurulamadı (${failedAttempts}. deneme), ${delay / 1000} sn sonra tekrar denenecek:`,
        error
      );
      retryTimer = setTimeout(start, delay);
    }
  };

  connection.onreconnecting(() => onStatusChange?.('Reconnecting'));

  connection.onreconnected(() => {
    onStatusChange?.('Connected');
    void runOnConnected();
  });

  // withAutomaticReconnect yalnızca kurulmuş bir bağlantı koptuğunda devreye girer.
  // Handshake reddi, onConnected hatası veya başka bir sebeple Disconnected'a
  // düşüldüğünde toparlanmayı sağlayan tek yol burası.
  connection.onclose(() => {
    if (disposed) return;
    onStatusChange?.('Disconnected');
    retryTimer = setTimeout(start, 3000);
  });

  void start();

  return () => {
    disposed = true;
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    void connection.stop().catch(() => {});
  };
}
